import React, { useState } from 'react';
import {
  ArrowDownToLine,
  Star,
  Users,
  CheckSquare,
  Sparkles,
  History,
  ShieldCheck,
} from 'lucide-react';
import { formatCoinAmount } from '@nova-mine/shared';
import { GlassCard } from '../components/common/GlassCard.js';
import { sound } from '../utils/sound.js';

interface ActivityItem {
  id: string;
  type: 'claim_main' | 'claim_auto' | 'claim_stars' | 'stars_purchase' | 'referral_bonus' | 'task_reward';
  title: string;
  amount: number;
  starsAmount?: number;
  timestamp: string;
  status: 'confirmed' | 'pending';
}

interface ActivityScreenProps {
  balance: number;
  starsMinersCount: number;
  referralCount: number;
}

export const ActivityScreen: React.FC<ActivityScreenProps> = ({
  balance,
  starsMinersCount,
  referralCount,
}) => {
  const [filter, setFilter] = useState<'all' | 'claims' | 'stars' | 'referrals' | 'tasks'>('all');

  const activities: ActivityItem[] = [
    {
      id: 'tx_01',
      type: 'claim_main',
      title: 'Main Rig Partial Claim',
      amount: Math.min(83.3333, balance > 0 ? balance * 0.2 : 45.2),
      timestamp: 'Today, 11:20 AM',
      status: 'confirmed',
    },
    ...(starsMinersCount > 0
      ? [
          {
            id: 'tx_stars_01',
            type: 'stars_purchase' as const,
            title: `Stars Miner Deployed (${starsMinersCount}x Unit)`,
            amount: 0,
            starsAmount: starsMinersCount * 10,
            timestamp: 'Yesterday, 04:15 PM',
            status: 'confirmed' as const,
          },
          {
            id: 'tx_stars_claim',
            type: 'claim_stars' as const,
            title: 'Stars Accelerator Hash Claim',
            amount: 4166.6667 * starsMinersCount,
            timestamp: 'Yesterday, 08:30 PM',
            status: 'confirmed' as const,
          },
        ]
      : []),
    ...(referralCount > 0
      ? [
          {
            id: 'tx_ref_01',
            type: 'referral_bonus' as const,
            title: 'Referral Bounty Grant',
            amount: 10000,
            timestamp: 'Oct 15, 02:40 PM',
            status: 'confirmed' as const,
          },
        ]
      : []),
    {
      id: 'tx_task_01',
      type: 'task_reward',
      title: 'Official Channel Quest Verification',
      amount: 50,
      timestamp: 'Oct 15, 10:10 AM',
      status: 'confirmed',
    },
    {
      id: 'tx_onboard',
      type: 'claim_main',
      title: 'Pioneer Genesis Miner Provisioned',
      amount: 0,
      timestamp: 'Registration Date',
      status: 'confirmed',
    },
  ];

  const filtered = activities.filter((act) => {
    if (filter === 'all') return true;
    if (filter === 'claims') return act.type.startsWith('claim');
    if (filter === 'stars') return act.type === 'stars_purchase' || act.type === 'claim_stars';
    if (filter === 'referrals') return act.type === 'referral_bonus';
    if (filter === 'tasks') return act.type === 'task_reward';
    return true;
  });

  const getIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'claim_main':
      case 'claim_auto':
      case 'claim_stars':
        return <ArrowDownToLine className="w-4 h-4 text-cyan-400" />;
      case 'stars_purchase':
        return <Star className="w-4 h-4 text-nova-gold fill-current" />;
      case 'referral_bonus':
        return <Users className="w-4 h-4 text-pink-400" />;
      case 'task_reward':
        return <CheckSquare className="w-4 h-4 text-purple-400" />;
      default:
        return <Sparkles className="w-4 h-4 text-gray-400" />;
    }
  };

  const handleFilterClick = (f: typeof filter) => {
    sound.playTap();
    setFilter(f);
  };

  return (
    <div className="w-full space-y-5 animate-fade-in pb-4">
      {/* 1. Header Card */}
      <GlassCard variant="deep" className="border-white/10 p-5 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 border border-indigo-400/35 flex items-center justify-center text-cyan-300">
            <History className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-base font-black text-white">Immutable Ledger & Activity</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Authoritative records of claims, Stars purchases & referral bonuses.
            </p>
          </div>
        </div>

        <div className="hidden sm:flex items-center space-x-1.5 text-emerald-400 text-xs font-bold px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25">
          <ShieldCheck className="w-4 h-4" />
          <span>Synced</span>
        </div>
      </GlassCard>

      {/* 2. Filter Tabs */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-1 scrollbar-none">
        {(['all', 'claims', 'stars', 'referrals', 'tasks'] as const).map((f) => (
          <button
            key={f}
            onClick={() => handleFilterClick(f)}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold capitalize transition-all whitespace-nowrap ${
              filter === f
                ? 'bg-gradient-to-r from-cyan-500/30 to-purple-500/30 text-white border border-cyan-400/50 shadow-sm'
                : 'bg-black/35 text-gray-400 border border-white/5 hover:text-white'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* 3. Transaction Items List */}
      <div className="space-y-2.5">
        {filtered.map((item) => (
          <GlassCard key={item.id} variant="deep" className="p-3.5 border-white/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  {getIcon(item.type)}
                </div>
                <div>
                  <h5 className="font-bold text-white text-xs">{item.title}</h5>
                  <span className="text-[10px] text-gray-500">{item.timestamp}</span>
                </div>
              </div>

              <div className="text-right">
                {item.starsAmount ? (
                  <span className="text-xs font-black font-mono text-nova-gold block">
                    -{item.starsAmount} ⭐
                  </span>
                ) : item.amount > 0 ? (
                  <span className="text-xs font-black font-mono text-cyan-300 block">
                    +{formatCoinAmount(item.amount, item.amount % 1 === 0 ? 0 : 2)} NOVA
                  </span>
                ) : (
                  <span className="text-xs font-bold text-gray-400 block">Genesis</span>
                )}
                <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">
                  {item.status}
                </span>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
};
